import Accordion from '@atoms/styled-accordion';
import Container from '@atoms/Container';
import SvgSafeGatsbyImage from '@atoms/SvgSafeGatsbyImage';
import ArticleCard from '@sections/ArticleCard';
import apCaseOnlyTitleTags from '@utils/apCaseOnlyTitleTags';
import paddingDefault from '@utils/paddingDefault';
import RichTextEditorStyles from '@utils/RichTextEditorStyles';
import { graphql } from 'gatsby';
import React from 'react';
import { colors } from '../primitives/colors';
import Link from '../primitives/link';
import { StyledButtonAsLink } from '../primitives/styled-button';

const IconBullets = ({ data }) => {
    const {
        headline,
        subheadline,
        copy,
        end_copy,
        blurbs,
        settings,
        button_link,
        spacing,
    } = data;
    const { dark_mode, one_column, number_of_grid_columns, style } = settings;

    function renderLink(link, link_as_text, icon_as_link) {
        if (!link || !link.title || icon_as_link) {
            return null;
        }

        let innerJsx;

        if (link_as_text) {
            innerJsx = (
                <Link
                    css={{ color: colors.primaryOrange }}
                    small
                    to={link.href}
                >
                    {link.title}
                </Link>
            );
        } else {
            innerJsx = (
                <StyledButtonAsLink small to={link.href}>
                    {link.title}
                </StyledButtonAsLink>
            );
        }

        return <div css={styles.innerJsxWrap}>{innerJsx}</div>;
    }

    function renderAsCards() {
        return (
            <div css={styles.blurbsWrap(one_column, dark_mode, number_of_grid_columns)}>
                {blurbs.map((blurb) => {
                    const { heading, content, icon, link } = blurb;
                    return (
                        <ArticleCard
                            key={heading}
                            article={{
                                title: heading,
                                featured_image: icon,
                                content,
                                url: link.href,
                            }}
                        />
                    );
                })}
            </div>
        );
    }

    function renderAsAccordion() {
        const questions = blurbs.map(({ heading, content, icon, link }) => ({
            question: heading,
            answer: content,
            icon,
            url: link.href,
        }));
        return (
            <Accordion
                faqs={{
                    questions: [{ questions }],
                }}
                onDark={dark_mode}
            />
        );
    }

    function renderAsDefault() {
        return (
            <div css={styles.blurbsWrap(one_column, dark_mode, number_of_grid_columns)}>
                {blurbs.map((blurb) => {
                    const {
                        heading,
                        content,
                        icon,
                        responsive,
                        icon_as_link,
                        link,
                        link_as_text,
                    } = blurb;
                    const icon_on_left = true;

                    const blurbIcon = (() => {
                        if (icon?.url) {
                            if (icon_as_link && link?.href) {
                                return (
                                    <Link to={link?.href || ''} css={styles.iconWrap(!!responsive, one_column, dark_mode, icon_on_left)}>
                                        <SvgSafeGatsbyImage image={icon} alt={icon.title} />
                                    </Link>
                                );
                            }
                            return (
                                <div css={styles.iconWrap(!!responsive, one_column, dark_mode, icon_on_left)}>
                                    <SvgSafeGatsbyImage image={icon} alt={icon.title} />
                                </div>
                            );
                        }
                        return null;
                    })();

                    return (
                        <div key={heading} css={styles.blurb(one_column, icon_on_left)}>
                            {blurbIcon}
                            <div>
                                {heading && (
                                    <h3 css={styles.blurbHeading(dark_mode)}>
                                        {apCaseOnlyTitleTags(heading)}
                                    </h3>
                                )}
                                <div
                                    css={[RichTextEditorStyles, styles.blurbContent(dark_mode)]}
                                    dangerouslySetInnerHTML={{ __html: content }}
                                />
                                {renderLink(link, link_as_text, (icon_as_link && !!icon?.url))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    function renderItems() {
        switch (style) {
            case 'default':
                return renderAsDefault();
            case 'cards':
                return renderAsCards();
            case 'accordion':
                return renderAsAccordion();
            default:
                return renderAsDefault();
        }
    }

    return (
        <section css={[paddingDefault(spacing), styles.bulletsSection(dark_mode)]}>
            <Container>
                <div css={styles.contentWrap(one_column)}>
                    <div css={styles.headingWrap(one_column)}>
                        <h2
                            css={styles.heading(dark_mode, one_column)}
                            dangerouslySetInnerHTML={{
                                __html: apCaseOnlyTitleTags(headline),
                            }}
                        />
                        {subheadline && (
                            <h3
                                css={styles.subheading(dark_mode, one_column)}
                                dangerouslySetInnerHTML={{
                                    __html: apCaseOnlyTitleTags(subheadline),
                                }}
                            />
                        )}
                        {copy && (
                            <div
                                css={[RichTextEditorStyles, styles.copy(dark_mode)]}
                                dangerouslySetInnerHTML={{ __html: copy }}
                            />
                        )}
                    </div>
                    {renderItems()}
                    {end_copy && (
                        <div
                            css={[RichTextEditorStyles, styles.copy(dark_mode)]}
                            dangerouslySetInnerHTML={{ __html: end_copy }}
                        />
                    )}
                    {button_link.title && (
                        <div css={styles.buttonLinkTitle}>
                            <StyledButtonAsLink to={button_link.href}>{button_link.title}</StyledButtonAsLink>
                        </div>
                    )}
                </div>
            </Container>
        </section>
    );
};

const styles = {
    innerJsxWrap: {
        display: 'flex',
        marginTop: '0.75rem',
        justifyContent: 'flex-start',
    },
    buttonLinkTitle: {
        display: 'flex',
        paddingTop: '2.5rem',
        justifyContent: 'center',
        '@media (min-width: 1024px)': {
            paddingTop: '9rem',

        },
    },
    bulletsSection: isDarkMode => ({
        color: isDarkMode ? colors.shadesWhite : colors.shadesBlack,
        backgroundColor: isDarkMode && colors.shadesBlack,
    }),
    contentWrap: isOneColumn => ({
        display: 'flex',
        flexDirection: 'column',
        '@media (min-width: 1280px)': !isOneColumn && {
            flexDirection: 'row',
            justifyContent: 'space-between',
        },
    }),
    headingWrap: isOneColumn => ({
        marginBottom: '2rem',
        width: '100%',
        '@media (min-width: 1280px)': !isOneColumn && {
            marginBottom: '0',
            marginRight: '5rem',
            maxWidth: '28rem',
        },
    }),
    heading: (isDarkMode, isOneColumn) => ({
        letterSpacing: '2.57px',
        fontSize: isDarkMode ? '32px' : '36px',
        color: (isDarkMode || isOneColumn) ? colors.primaryOrange : colors.shadesBlack,
        '@media (min-width: 1024px)': !isDarkMode && {
            fontSize: '32px',
        },

    }),
    subheading: (isDarkMode, isOneColumn) => ({
        fontSize: '21px',
        color: isDarkMode ? colors.shadesWhite : isOneColumn ? colors.shadesBlack : colors.primaryOrange,
    }),
    copy: isDarkMode => ({
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        marginTop: isDarkMode && '1.75rem',
        whiteSpace: !isDarkMode && 'pre-line',
        '@media (min-width: 1280px)': !isDarkMode && {
            fontSize: '1rem',
            lineHeight: '1.5rem',
        },

    }),
    blurbsWrap: (isOneColumn, isDarkMode, numberOfGridColumns) => ({
        display: 'grid', // default
        gridTemplateColumns: 'repeat(1, minmax(0, 1fr))', // default
        gap: '2rem', // default
        '@media (min-width: 768px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', // default
            gap: '2rem', // default
        },
        '@media (min-width: 1024px)': {
            width: '100%', // default
            columnGap: isDarkMode ? '2rem' : (numberOfGridColumns === 4 || numberOfGridColumns === 5) ? '1.5rem' : '3rem',
            rowGap: '2rem', // this is true if dark or not, therefore, it is default
            gridTemplateColumns: isOneColumn ? (() => {
                if (numberOfGridColumns % 2 === 0) {
                    return `repeat(${numberOfGridColumns}, minmax(0, 1fr))`;
                }
                return 'repeat(3, minmax(0, 1fr))';
            })() : `repeat(${numberOfGridColumns}, minmax(0, 1fr))`,
        },
    }),
    blurb: (isOneColumn, iconIsOnLeft) => ({
        display: 'flex', // default
        alignItems: 'flex-start', // default
        flexDirection: isOneColumn ? 'column' : iconIsOnLeft && 'row',
        marginLeft: !isOneColumn && '1.25rem',
        '@media (min-width: 1024px)': {
            flexDirection: isOneColumn ? 'row' : !iconIsOnLeft && 'column',
        },
    }),
    blurbHeading: isDarkMode => ({
        marginBottom: !isDarkMode ? '1rem' : '0.5rem',
        fontSize: !isDarkMode ? '1.25rem' : '1.125rem',
        lineHeight: '1.75rem',
        fontWeight: 600,
        '@media (min-width: 1024px)': {
            fontSize: !isDarkMode ? '1rem' : '1.25rem',
            lineHeight: !isDarkMode ? '1.5rem' : '1.75rem',
            marginBottom: !isDarkMode && '0.5rem',
        },
    }),
    blurbContent: isDarkMode => ({
        letterSpacing: '0.29px',
        fontSize: !isDarkMode ? '1.25rem' : '1.125rem',
        lineHeight: '1.75rem',
        marginBottom: 'auto',
        '@media (min-width: 1024px)': {
            lineHeight: '1.5rem',
            maxWidth: isDarkMode ? '262px' : '255px',
            fontSize: isDarkMode ? '1.25rem' : '1rem',

        },
    }),
    iconWrap: (isResponsive, isOneColumn, isDarkMode, iconIsOnLeft) => ({
        position: 'relative',
        marginBottom: '0.5rem',
        width: '100%',
        maxWidth: (!isResponsive || !isOneColumn) && '40px',
        marginRight: iconIsOnLeft && '1.25rem',
        flexShrink: 0,
        '@media (min-width: 1024px)': {
            maxWidth: !isResponsive && '64px',
            minWidth: !isOneColumn && '60px',
        },
        '@media (min-width: 768px)': {
            marginBottom: '0',
            top: !isOneColumn ? 0 : '-0.5rem',
            marginRight: isDarkMode ? '1.75rem' : '0.25rem',
        },
    }),
};

export const query = graphql`
  fragment IconBulletsData on CmsPageBuilderBlocks {
    icon_bullets {
      headline
      subheadline
      copy
      end_copy
      spacing {
        top_gutter
        bottom_gutter
      }
      settings {
        dark_mode
        one_column
        number_of_grid_columns
        style
      }
      blurbs {
        heading
        content
        icon {
          filename
          title
          url
          contentStackAsset
        }
        responsive
        icon_on_left
        link {
          href
          title
        }
        icon_as_link
        link_as_text
      }
      button_link {
        href
        title
      }
    }
  }
`;

export default IconBullets;
